/**
 * Echo Hunt -- settings dev tool: the weaver (P8, §8.1 + §8.2).
 *
 * This panel builds a P7 bullet pool, weaves it into a round, and shows the result with the
 * labels hidden behind a toggle -- exactly as the Play tab will show it in P9.
 *
 * The world pool, the schematic rules and the character preview live in their OWN tabs (`WorldsPanel`,
 * `CharactersPanel`): they are things you configure once and then leave alone, while this panel is what
 * you press repeatedly while tuning prompts. Mixing the two made the Settings tab a long scroll with no
 * clear focus.
 *
 * ## Why the labels are hidden by default here too (§16.0)
 *
 * The whole point of the weave is that a woven story should read the same whichever bullet came from
 * where. A dev tool that printed `real`/`fake` next to each line would train the reader to use the
 * labels instead of the text -- which is precisely the habit the game exists to build the opposite of.
 * The toggle is there for when you have already formed an impression.
 */
import React, {useState} from "react";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Text} from "react-vcomponents";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {InfoButton} from "web-vcore";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {BuildBulletPool_Live, IsBulletPoolReady} from "../../../EchoHunt/Reshape_Live.js";
import {GetWeaveContext_Live, GetWorldPool_Live, WeavePool_Live} from "../../../EchoHunt/Weave_Live.js";
import {RoundSlot} from "../../../EchoHunt/Types.js";
import {BridgingSpread, ComputeBridgingSpread, DescribeWeaveFailure, ToWovenHuntSlotView, WeaveResult} from "../../../EchoHunt/Weave.js";

/** How many bullets the dev weave uses. Small enough to read in one screen, big enough that the weaver
 * has something to actually order. */
const DEV_WEAVE_BULLET_COUNT = 6;

export const WeaveDevPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<WeaveResult|null>(null);
	const [wovenSlots, setWovenSlots] = useState<RoundSlot[]|null>(null);
	const [poolSlots, setPoolSlots] = useState<RoundSlot[]|null>(null);
	const [spread, setSpread] = useState<BridgingSpread|null>(null);
	const [error, setError] = useState<string|null>(null);
	const [showLabels, setShowLabels] = useState(false);

	const worldPool = GetWorldPool_Live();
	const ready = profile != null && IsBulletPoolReady() && worldPool.length > 0;

	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile to use the weaver.</Text>;
	}

	const runWeave = async()=>{
		setRunning(true);
		setResult(null);
		setWovenSlots(null);
		setPoolSlots(null);
		setSpread(null);
		setError(null);
		setShowLabels(false); // always start hidden, even on a rebuild

		try {
			const pool = await BuildBulletPool_Live({N: DEV_WEAVE_BULLET_COUNT, X: .65, rng: Math.random, seedingEnabled: true});
			if (pool == null) {
				setError("The corpus or the word list is not ready (see the bullet-pool panel above).");
				setRunning(false);
				return;
			}
			setPoolSlots(pool.slots);

			// wrapped in `GetAsync`: this is a click handler, so nothing catches the cold-read bail that
			// `echoStories` would throw (no other tab renders it, so it is never subscribed to beforehand).
			// One wrap covers every accessor the context reads, profile included.
			const context = await GetAsync(()=>GetWeaveContext_Live({rng: Math.random}));
			if (context == null) {
				setError("No world in the pool and no active story -- nothing to weave the bullets into.");
				setRunning(false);
				return;
			}

			const woven = await WeavePool_Live(pool.slots, {context});
			setResult(woven.result);
			setWovenSlots(woven.slots);
			if (woven.slots != null) setSpread(ComputeBridgingSpread(woven.slots));
		} catch (ex) {
			setError(`${ex}`);
		}
		setRunning(false);
	};

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Weaver (dev preview)</Text>
				<InfoButton ml={5} text={[
					"Builds a P7 bullet pool and then WEAVES it into a story round (§8.1, P8).",
					"",
					"The weaver is blind to the labels: it receives `{id, text}` pairs only, so it cannot treat a real",
					"bullet differently from a fake one. It is the ONE step that sees the world, the character and the",
					"storyline summary (invariant #4).",
					"",
					"The locked-ID contract is enforced by `ValidateWeave`: the same ids out as in, the same COUNT (so a",
					"merge or a split fails, not just a dropped id), no duplicates, non-empty text, and every woven bullet",
					"still passing the §7 house-style check. One informed re-ask, then the round FAILS -- a half-woven",
					"story would show you text the weaver never wrote.",
					"",
					"Labels are hidden by default, on purpose. Read the round first; the toggle is for afterwards.",
				].join("\n")}/>
			</Row>

			<Column mt={6} style={{gap: 2}}>
				<Text style={{opacity: .8}}>{`World pool: ${worldPool.length} world(s) (edit them on the Worlds tab)`}</Text>
				{worldPool.length == 0 && <Text style={{color: "orange"}}>No worlds yet -- they are seeded automatically; if this persists, the profile write failed.</Text>}
				{!IsBulletPoolReady() && <Text style={{color: "orange"}}>Not ready -- needs an indexed corpus and a loaded word list.</Text>}
			</Column>

			<Row mt={8} center style={{gap: 8}}>
				<Button text={running ? "Weaving…" : `Build ${DEV_WEAVE_BULLET_COUNT}-bullet pool and weave it`} enabled={ready && !running} onClick={runWeave}/>
				{running && <ProgressSpinner/>}
				<CheckBox text="Show labels" value={showLabels} onChange={setShowLabels}/>
			</Row>

			{error != null && <Text sel mt={6} style={{color: "red"}}>{error}</Text>}

			{result != null && !result.validation.ok &&
				<Column sel mt={8} style={{gap: 2}}>
					<Text style={{fontWeight: "bold", color: "red"}}>The weave FAILED -- this round could not be played:</Text>
					<Text style={{fontSize: 11, opacity: .85, whiteSpace: "pre-wrap"}}>{DescribeWeaveFailure(result.validation, result.attempts)}</Text>
				</Column>}

			{wovenSlots != null && result?.output != null &&
				<WovenStoryView
					poolSlots={poolSlots}
					wovenSlots={wovenSlots}
					doorDescription={result.output.doorOffer?.description ?? null}
					summaryOut={result.output.summaryOut}
					spread={spread}
					showLabels={showLabels}/>}
		</>
	);
});

// the woven round
// ==========

const WovenStoryView = observer_mgl((props: {
	poolSlots: RoundSlot[]|null,
	wovenSlots: RoundSlot[],
	doorDescription: string|null,
	summaryOut: string,
	spread: BridgingSpread|null,
	showLabels: boolean,
})=>{
	const {poolSlots, wovenSlots, doorDescription, summaryOut, spread, showLabels} = props;
	// the label-free view model is what P9's Play tab will receive (§16.0)
	const views = ToWovenHuntSlotView(wovenSlots);

	return (
		<Column sel mt={10} style={{gap: 8}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{`The round (${views.length} bullets)`}</Text>
				{spread != null &&
					<Text style={{fontSize: 11, opacity: .7}}>
						{`rewritten: mean ${Math.round(spread.meanRewriteFraction * 100)}%, max ${Math.round(spread.maxRewriteFraction * 100)}%; untouched ${spread.untouchedCount}, heavy ${spread.heavyCount}`}
					</Text>}
			</Row>

			<Column style={{gap: 4}}>
				{views.map(view=>{
					const slot = wovenSlots.find(a=>a.slotId == view.slotId)!;
					const original = poolSlots?.find(a=>a.slotId == view.slotId) ?? null;
					return (
						<Column key={view.slotId} style={{padding: 6, borderRadius: 4, background: "rgba(255,255,255,.05)"}}>
							<Row center style={{gap: 8, flexWrap: "wrap"}}>
								<Text style={{fontSize: 11, opacity: .5}}>{view.slotId.substring(0, 8)}</Text>
								{showLabels &&
									<Text style={{fontSize: 11, color: slot.sourceKind == "real" ? "lightgreen" : "violet"}}>{slot.sourceKind}</Text>}
								{slot.wovenEditDistance != null &&
									<Text style={{fontSize: 11, opacity: .5}}>{`rewritten by ${slot.wovenEditDistance} word(s)`}</Text>}
							</Row>
							<Text style={{marginTop: 2}}>{view.text}</Text>
							{/* the pre-weave text is shown only with labels on: comparing them is useful, but
							    the point of the preview is to read the round as the player will */}
							{showLabels && original != null &&
								<Text style={{fontSize: 11, opacity: .5, marginTop: 2}}>{`before: ${original.reshapedText}`}</Text>}
						</Column>
					);
				})}
			</Column>

			<Column style={{gap: 2}}>
				<Text style={{fontWeight: "bold"}}>Door offer (§8.3)</Text>
				<Text style={{opacity: doorDescription != null ? 1 : .6}}>{doorDescription ?? "(none -- the round does not end at a way out)"}</Text>
			</Column>

			<Column style={{gap: 2}}>
				<Text style={{fontWeight: "bold"}}>Storyline summary for the next round (§8.1)</Text>
				<Text style={{opacity: .85}}>{summaryOut}</Text>
			</Column>
		</Column>
	);
});
